import {match, Pattern} from 'ts-pattern';

import {dissoc, isNil, sum, uniq} from 'ramda';

import type {PricelistVersionRequestBody} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {MAX_BATCH_OPERATIONS} from './buildBatchOperations';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

/**
 * A batch of the group that was sent, whose response was lost and whose read-back failed (spec
 * 7.5): it may be written. Confirm reads it back before it sends anything again.
 */
export type UnknownBatchOutcome = {
  sent: PricelistVersionRequestBody;
  entries: PricedItemBatchEntry[];
};

/** The open group of one model year: changes that are not sent until the user confirms them (D107). */
export type PricedItemGroup = {
  modelYearId: string;
  entries: PricedItemBatchEntry[];
  unknownOutcome?: UnknownBatchOutcome;
};

/**
 * The refusal of a publication switch of a stored trim and a new colour or package offered on that
 * trim, in either order. It names the trim, so the message can say which one.
 */
export type TrimSwitchRefusal = {
  reason: 'trimSwitchWithNewItemRow';
  trim: NonNullable<PricedItemBatchEntry['switchedTrim']>;
};

/**
 * The answer of `add`. Every value except `'added'` is a refusal: the BE refuses that pair in one
 * batch (`duplicateTarget`, `targetCreatedInBatch`, `matrixPutNotAlone`, `trimInMatrix`,
 * `trimSwitchWithNewItemRow`, `duplicateValue`), so the group refuses it at add time and adds
 * nothing.
 */
export type AddResult =
  | 'added'
  | 'targetTaken'
  | 'matrixConflict'
  | 'trimInMatrix'
  | TrimSwitchRefusal
  | 'valueTaken'
  | 'tooManyChanges';

type Listener = () => void;

const isMatrixPut = (entry: PricedItemBatchEntry) => pricedItemGroupKeys.isMatrixPut(entry.key);
const isCellPatch = (entry: PricedItemBatchEntry) => pricedItemGroupKeys.isCellPatch(entry.key);

const sharesCell = (left: PricedItemBatchEntry, right: PricedItemBatchEntry) =>
  (left.matrixCells ?? []).some((cell) =>
    (right.matrixCells ?? []).some(
      (other) => other.trimId === cell.trimId && other.engineId === cell.engineId
    )
  );

const targetKeysOf = (entry: PricedItemBatchEntry) => [entry.key, ...(entry.extraKeys ?? [])];

/** An extra key of one entry is a key or an extra key of the other. */
const sharesExtraKey = (left: PricedItemBatchEntry, right: PricedItemBatchEntry) =>
  (left.extraKeys ?? []).some((key) => targetKeysOf(right).includes(key)) ||
  (right.extraKeys ?? []).some((key) => targetKeysOf(left).includes(key));

/** New matrix cells merge into one entry, unless a cell has the trim and engine of a grouped cell. */
const takesTarget = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries.some(
    (grouped) =>
      (grouped.key === entry.key && (!isMatrixPut(entry) || sharesCell(grouped, entry))) ||
      sharesExtraKey(grouped, entry)
  );

/**
 * The id in a target key, as `<kind>:<id>`. A create key (`<kind>:new:<id>`) names no stored
 * target.
 */
const targetIdOf = (key: string) => key.slice(key.indexOf(':') + 1);

/**
 * An update, archive or assignment replace of an item that a create of the group makes. The BE refuses
 * it (`targetCreatedInBatch`): the item has no stored version to send yet.
 */
const changesCreatedItem = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries.some(
    (grouped) => !isNil(grouped.createdId) && grouped.createdId === targetIdOf(entry.key)
  );

const mixesMatrixWrites = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries.some(
    (grouped) =>
      (isMatrixPut(grouped) && isCellPatch(entry)) || (isCellPatch(grouped) && isMatrixPut(entry))
  );

const changesMatrixTrim = (trimEntry: PricedItemBatchEntry, matrixEntry: PricedItemBatchEntry) =>
  !isNil(trimEntry.trimRecordId) &&
  (matrixEntry.matrixTrimIds ?? []).includes(trimEntry.trimRecordId);

const putsTrimInMatrix = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries.some((grouped) => changesMatrixTrim(grouped, entry) || changesMatrixTrim(entry, grouped));

/** The trim of `switchEntry` when `newItemEntry` offers a new colour or package on it. */
const offersOnSwitchedTrim = (
  switchEntry: PricedItemBatchEntry,
  newItemEntry: PricedItemBatchEntry
) =>
  !isNil(switchEntry.switchedTrim) &&
  (newItemEntry.newItemTrimIds ?? []).includes(switchEntry.switchedTrim.id)
    ? switchEntry.switchedTrim
    : undefined;

/**
 * The trim whose publication switch meets a new colour or package offered on it, in either order.
 * The BE refuses the pair (`trimSwitchWithNewItemRow`): the switch does not see the rows of an item
 * that the same batch creates, so their History rows would be missing.
 */
const switchedTrimWithNewItem = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries
    .map((grouped) => offersOnSwitchedTrim(grouped, entry) ?? offersOnSwitchedTrim(entry, grouped))
    .find((trim) => !isNil(trim));

const takesValue = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  entries.some((grouped) =>
    (grouped.usedValues ?? []).some((value) => (entry.usedValues ?? []).includes(value))
  );

/**
 * The entries a merged matrix entry was made of, so a confirm takes out only the cells it sent
 * (`removeSent`). An entry that was not merged is its own only part.
 */
const mergedParts = new WeakMap<PricedItemBatchEntry, PricedItemBatchEntry[]>();
const partsOf = (entry: PricedItemBatchEntry) => mergedParts.get(entry) ?? [entry];

/**
 * One entry of several matrix cells. Its `build` runs the `build` of both parts; buildBatchOperations
 * merges their `variantMatrix.put` operations into one.
 */
const mergeCells = (grouped: PricedItemBatchEntry, entry: PricedItemBatchEntry) => {
  const merged: PricedItemBatchEntry = {
    ...grouped,
    items: [...grouped.items, ...entry.items],
    matrixCells: [...(grouped.matrixCells ?? []), ...(entry.matrixCells ?? [])],
    matrixTrimIds: uniq([...(grouped.matrixTrimIds ?? []), ...(entry.matrixTrimIds ?? [])]),
    build: async () => {
      const [first, second] = await Promise.all([grouped.build(), entry.build()]);
      return {
        items: [...first.items, ...second.items],
        operations: [...first.operations, ...second.operations],
      };
    },
  };
  mergedParts.set(merged, [...partsOf(grouped), ...partsOf(entry)]);
  return merged;
};

const withEntry = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  isMatrixPut(entry) && entries.some((grouped) => grouped.key === entry.key)
    ? entries.map((grouped) => (grouped.key === entry.key ? mergeCells(grouped, entry) : grouped))
    : [...entries, entry];

/**
 * Each entry sends its `operationCount` operations (one when unset), and the new matrix cells merge
 * into one entry with one `variantMatrix.put`.
 */
const exceedsBatch = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  sum(withEntry(entries, entry).map((grouped) => grouped.operationCount ?? 1)) >
  MAX_BATCH_OPERATIONS;

const checkEntry = (entries: PricedItemBatchEntry[], entry: PricedItemBatchEntry) =>
  match({entry, switchedTrim: switchedTrimWithNewItem(entries, entry)})
    .returnType<AddResult>()
    .when(
      ({entry: candidate}) =>
        takesTarget(entries, candidate) || changesCreatedItem(entries, candidate),
      () => 'targetTaken'
    )
    .when(
      ({entry: candidate}) => mixesMatrixWrites(entries, candidate),
      () => 'matrixConflict'
    )
    .when(
      ({entry: candidate}) => putsTrimInMatrix(entries, candidate),
      () => 'trimInMatrix'
    )
    .with({switchedTrim: Pattern.nonNullable}, ({switchedTrim}) => ({
      reason: 'trimSwitchWithNewItemRow',
      trim: switchedTrim,
    }))
    .when(
      ({entry: candidate}) => takesValue(entries, candidate),
      () => 'valueTaken'
    )
    .when(
      ({entry: candidate}) => exceedsBatch(entries, candidate),
      () => 'tooManyChanges'
    )
    .otherwise(() => 'added');

let groups: Readonly<Record<string, PricedItemGroup>> = {};
const listeners = new Set<Listener>();

const setGroups = (next: Readonly<Record<string, PricedItemGroup>>) => {
  groups = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The same reference until the group of that year changes, as `useSyncExternalStore` needs. */
const get = (modelYearId: string): PricedItemGroup | undefined => groups[modelYearId];

const open = (modelYearId: string) => {
  if (!isNil(groups[modelYearId])) {
    return;
  }
  setGroups({...groups, [modelYearId]: {modelYearId, entries: []}});
};

/** The entries of the group with `entries` added, or the first refusal. Changes nothing. */
const checked = (group: PricedItemGroup, entries: PricedItemBatchEntry[]) =>
  entries.reduce<{result: AddResult; entries: PricedItemBatchEntry[]}>(
    (current, entry) => {
      if (current.result !== 'added') {
        return current;
      }
      const result = checkEntry(current.entries, entry);
      return {
        result,
        entries: result === 'added' ? withEntry(current.entries, entry) : current.entries,
      };
    },
    {result: 'added', entries: group.entries}
  );

const groupOf = (modelYearId: string) => {
  const group = groups[modelYearId];
  if (isNil(group)) {
    throw new Error('Open the group of the model year before adding changes to it.');
  }
  return group;
};

/** What `add` would answer, without adding anything. */
const check = (modelYearId: string, entries: PricedItemBatchEntry[]) =>
  checked(groupOf(modelYearId), entries).result;

/** Checks every entry before it adds one, so a refused call adds nothing. */
const add = (modelYearId: string, entries: PricedItemBatchEntry[]) => {
  const group = groupOf(modelYearId);
  const outcome = checked(group, entries);
  if (outcome.result === 'added') {
    setGroups({...groups, [modelYearId]: {...group, entries: outcome.entries}});
  }
  return outcome.result;
};

/**
 * The entries of the group whose parts pass `keep`: a matrix entry keeps the cells merged into it
 * from other entries, and goes when none is left.
 */
const entriesKeeping = (
  entries: PricedItemBatchEntry[],
  keep: (part: PricedItemBatchEntry) => boolean
) =>
  entries.flatMap((entry) => {
    const parts = partsOf(entry);
    const kept = parts.filter(keep);
    if (kept.length === parts.length) {
      return [entry];
    }
    const [first, ...rest] = kept;
    return isNil(first) ? [] : [rest.reduce(mergeCells, first)];
  });

/** The entries of the group without `taken`, by reference. */
const entriesWithout = (entries: PricedItemBatchEntry[], taken: PricedItemBatchEntry[]) => {
  const takenParts = new Set(taken.flatMap(partsOf));
  return entriesKeeping(entries, (part) => !takenParts.has(part));
};

const namesCreatedItem = (part: PricedItemBatchEntry, createdId: string | undefined) =>
  !isNil(createdId) &&
  [...(part.matrixTrimIds ?? []), ...(part.referencedIds ?? [])].includes(createdId);

/**
 * Takes out the entry of `key`. For a create it also takes out the matrix cells and the entries that
 * name the created item (a colour create with its trims, a package write with its trims), as the BE
 * answers 404 for a reference to a trim that the batch does not create.
 */
const remove = (modelYearId: string, key: string) => {
  const group = groups[modelYearId];
  if (isNil(group)) {
    return;
  }
  const createdId = group.entries.find((entry) => entry.key === key)?.createdId;
  setGroups({
    ...groups,
    [modelYearId]: {
      ...group,
      entries: entriesKeeping(
        group.entries,
        (part) => part.key !== key && !namesCreatedItem(part, createdId)
      ),
    },
  });
};

const discard = (modelYearId: string) => {
  if (isNil(groups[modelYearId])) {
    return;
  }
  setGroups(dissoc(modelYearId, groups));
};

/**
 * Takes out the entries that a confirmed batch sent, by reference, and closes the group when none is
 * left. A change added while the batch was under review stays, also a matrix cell that merged into
 * a sent matrix entry meanwhile. The outcome of the sent batch is known now.
 */
const removeSent = (modelYearId: string, sent: PricedItemBatchEntry[]) => {
  const group = groups[modelYearId];
  if (isNil(group)) {
    return;
  }
  const left = entriesWithout(group.entries, sent);
  if (left.length === 0) {
    discard(modelYearId);
    return;
  }
  setGroups({...groups, [modelYearId]: {modelYearId, entries: left}});
};

/** Takes back the entries of an `add` whose save failed, by reference; the group stays open. */
const takeBack = (modelYearId: string, added: PricedItemBatchEntry[]) => {
  const group = groups[modelYearId];
  if (isNil(group)) {
    return;
  }
  setGroups({
    ...groups,
    [modelYearId]: {...group, entries: entriesWithout(group.entries, added)},
  });
};

/** Keeps a sent batch whose outcome is unknown, or forgets it once a read-back found it not written. */
const setUnknownOutcome = (
  modelYearId: string,
  unknownOutcome: UnknownBatchOutcome | undefined
) => {
  const group = groups[modelYearId];
  if (isNil(group)) {
    return;
  }
  setGroups({
    ...groups,
    [modelYearId]: isNil(unknownOutcome)
      ? {modelYearId, entries: group.entries}
      : {...group, unknownOutcome},
  });
};

/**
 * Module store of the open groups, keyed by model year id. It lives outside the React tree, so editors
 * that `openDialog` renders outside the model year shell see the group too (spec 7.3).
 */
export const pricedItemGroupStore = {
  subscribe,
  get,
  open,
  check,
  add,
  takeBack,
  remove,
  removeSent,
  setUnknownOutcome,
  discard,
};
